import React, { useRef } from "react";
import { Overlay, Popover } from "react-bootstrap";

interface EnrollmentPopoverProps {
  show: boolean;
  target: HTMLElement | null;
  enrollments: any[];
  onClose: () => void;
  onQuickView: (enrollmentId: number) => void;
  enquiryId?: number | null;
  onCreateEnrollment?: (enquiryId: number) => void;
}

/* ─── shared style helpers (mirrors SearchResults for visual consistency) ─── */

const iconBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 26,
  height: 26,
  borderRadius: 6,
  cursor: "pointer",
  border: "none",
  background: "transparent",
  transition: "background-color .12s",
};

/** Soft pastel pill tones — same palette as SearchResults so the popover and
 *  the row it expands from share a visual language. */
const statusPalette = (raw: string): { bg: string; fg: string; border: string } => {
  const s = String(raw || "").toLowerCase();
  if (!s) return { bg: "#fff7ed", fg: "#9a3412", border: "#fed7aa" };
  if (s.includes("dropout") || s.includes("inactive") || s.includes("cancel")) {
    return { bg: "#fef2f2", fg: "#991b1b", border: "var(--status-danger-border)" };
  }
  if (s.includes("hold") || s.includes("pending") || s.includes("draft")) {
    return { bg: "#fefce8", fg: "#854d0e", border: "var(--status-warning-border)" };
  }
  if (s.includes("regular") || s.includes("active") || s.includes("enroll")) {
    return { bg: "#ecfdf5", fg: "#065f46", border: "var(--status-success-border)" };
  }
  return { bg: "#eff6ff", fg: "#1e3a8a", border: "#bfdbfe" };
};

const EnrollmentPopover: React.FC<EnrollmentPopoverProps> = ({
  show,
  target,
  enrollments,
  onClose,
  onQuickView,
  enquiryId,
  onCreateEnrollment,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  if (!target || enrollments.length === 0) return null;

  return (
    <div ref={containerRef}>
      <Overlay
        show={show}
        target={target}
        placement="bottom-start"
        container={containerRef}
        rootClose
        onHide={onClose}
      >
        <Popover
          id="enrollment-popover"
          style={{
            maxWidth: 420,
            minWidth: 320,
            border: "1px solid var(--border-default)",
            borderRadius: 12,
            boxShadow: "0 12px 32px rgba(15,23,42,0.16)",
            overflow: "hidden",
          }}
        >
          {/* Header — slate band with count and close */}
          <Popover.Header
            as="div"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 14px",
              background: "var(--surface-page)",
              borderBottom: "1px solid var(--border-default)",
              fontSize: "0.72rem",
              fontWeight: 700,
              color: "var(--text-body)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            <span>
              Enrollments
              <span
                style={{
                  marginLeft: 8,
                  padding: "1px 8px",
                  borderRadius: 10,
                  background: "var(--surface-sunken)",
                  color: "var(--text-strong)",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  letterSpacing: 0,
                }}
              >
                {enrollments.length}
              </span>
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                ...iconBtnStyle,
                color: "var(--text-muted)",
                width: 24,
                height: 24,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-sunken)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <i className="fa fa-times" style={{ fontSize: 12 }} />
            </button>
          </Popover.Header>

          <Popover.Body style={{ padding: 0, background: "var(--surface-card)" }}>
            {enrollments.map((enr: any, idx: number) => {
              const pal = statusPalette(enr.statusName);
              const isLast = idx === enrollments.length - 1;
              return (
                <div
                  key={enr.enrollmentId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    borderBottom: isLast ? "none" : "1px solid #f1f5f9",
                    gap: 10,
                    transition: "background-color .12s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-page)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "var(--surface-card)")}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 4,
                        flexWrap: "wrap",
                      }}
                    >
                      {enr.studentCode && (
                        <span
                          style={{
                            fontWeight: 700,
                            color: "var(--text-strong)",
                            fontSize: "0.85rem",
                          }}
                        >
                          {enr.studentCode}
                        </span>
                      )}
                      {enr.statusName && (
                        <span
                          style={{
                            padding: "1px 8px",
                            borderRadius: 10,
                            background: pal.bg,
                            color: pal.fg,
                            border: `1px solid ${pal.border}`,
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          {enr.statusName}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        color: "var(--text-muted)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: 1.45,
                      }}
                      title={[enr.batchName, enr.yearName, enr.schoolName]
                        .filter(Boolean)
                        .join(" • ")}
                    >
                      {enr.batchName && (
                        <span style={{ color: "var(--text-strong)", fontWeight: 600 }}>
                          {enr.batchName}
                        </span>
                      )}
                      {enr.yearName && (
                        <>
                          <span style={{ margin: "0 6px", color: "var(--text-faint)" }}>•</span>
                          <span>{enr.yearName}</span>
                        </>
                      )}
                      {enr.schoolName && (
                        <>
                          <span style={{ margin: "0 6px", color: "var(--text-faint)" }}>•</span>
                          <span>{enr.schoolName}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 2,
                      flexShrink: 0,
                      padding: 2,
                      background: "var(--surface-sunken)",
                      borderRadius: 6,
                    }}
                  >
                    <button
                      type="button"
                      title="Quick View"
                      style={{ ...iconBtnStyle, color: "var(--brand-primary-hover)" }}
                      onClick={() => {
                        onQuickView(enr.enrollmentId);
                        onClose();
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--status-info-bg)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <i className="fa fa-eye" style={{ fontSize: 12 }} />
                    </button>
                    <button
                      type="button"
                      title="Open Full Page"
                      style={{ ...iconBtnStyle, color: "var(--status-success)" }}
                      onClick={() => {
                        window.open(`/enrollment/view/${enr.enrollmentId}`, "_blank");
                        onClose();
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--status-success-bg)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <i className="fa fa-external-link" style={{ fontSize: 11 }} />
                    </button>
                  </div>
                </div>
              );
            })}
            {onCreateEnrollment && enquiryId && (
              <div
                style={{
                  padding: "10px 14px",
                  background: "var(--surface-page)",
                  borderTop: "1px solid var(--border-default)",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    onCreateEnrollment(enquiryId);
                    onClose();
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    width: "100%",
                    padding: "8px 12px",
                    background:
                      "var(--gradient-brand)",
                    color: "var(--text-on-brand)",
                    border: "none",
                    borderRadius: 8,
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "0 1px 2px rgba(15,23,42,0.08)",
                    transition: "transform .06s, box-shadow .12s, opacity .12s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "0.92";
                    e.currentTarget.style.boxShadow = "0 4px 10px rgba(79,70,229,0.25)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "1";
                    e.currentTarget.style.boxShadow = "0 1px 2px rgba(15,23,42,0.08)";
                  }}
                >
                  <i className="fa fa-plus" style={{ fontSize: 11 }} />
                  New Enrollment
                </button>
              </div>
            )}
          </Popover.Body>
        </Popover>
      </Overlay>
    </div>
  );
};

export default EnrollmentPopover;
